import React, { useEffect, useState, useRef, useCallback } from 'react';
import { object, string } from 'yup';
import { getReservationsPage } from './graphql/queries';
import { deleteReservation } from './graphql/mutations';
import { type Reservation, type GetReservationsPageQuery } from './API';
import { Alert, Box, Container, Table, TableBody, TableCell, TableHead, TableRow, Button, Chip, Snackbar, Stack, Typography, TableContainer, LinearProgress, IconButton, Tooltip } from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded';
import { useClient } from './ClientContext';

const appointmentSchema = object({
  appointmentData: object({
    hospitalName: string().optional(),
    nickname: string().optional(),
    patientCardNumber: string().optional(),
    name: string().optional(),
    partOfDay: string().oneOf(['0', '1']).optional(),
  }).required(),
});

function patientDetails(raw: string) {
  try {
    const data: unknown = JSON.parse(raw);
    const { appointmentData } = appointmentSchema.validateSync(data, { strict: true });
    return {
      hospital: appointmentData.hospitalName === 'sekine-jibika' ? 'せきね耳鼻咽喉科' : appointmentData.hospitalName || '不明',
      patient: appointmentData.name || [appointmentData.patientCardNumber, appointmentData.nickname].filter(Boolean).join(' '),
      partOfDay: appointmentData.partOfDay || '',
    };
  } catch {
    return { hospital: '情報を読み取れません', patient: '不明', partOfDay: '' };
  }
}

function statusDetails(event: Reservation) {
  if (event.Status === 'pending') return { label: '実行待ち', color: 'default' } as const;
  if (event.Status === 'processing') {
    const started = Date.parse(event.StartedAt || '');
    if (Number.isFinite(started) && Date.now() - started < 10 * 60 * 1000) {
      return { label: '予約処理中', color: 'info' } as const;
    }
  }
  if (event.Status === 'succeeded') return { label: '予約成立', color: 'success' } as const;
  if (event.Status === 'failed') return { label: '予約失敗', color: 'error' } as const;
  return { label: '結果未確認', color: 'warning' } as const;
}

function canCancel(event: Reservation) {
  if (event.Status === 'pending') return true;
  const today = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Tokyo' }).format(new Date());
  return event.Status == null && /^\d{4}-\d{2}-\d{2}$/.test(event.ReservationDate) && event.ReservationDate > today;
}

const ReservationConfirm = () => {
  const [events, setEvents] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState<string | null>(null);
  const [notice, setNotice] = useState('');
  const previous = useRef<Map<string, string | null | undefined> | null>(null);
  const inFlight = useRef(false);
  const listVersion = useRef(0);
  const deletingRef = useRef(false);
  const generation = useRef(0);
  const { client, session } = useClient();
  const sessionKind = useRef(session.kind);
  useEffect(() => { sessionKind.current = session.kind; }, [session.kind]);

  const fetchEvents = useCallback(async (force = false) => {
    if (!client || sessionKind.current !== 'ready' || (inFlight.current && !force)) return;
    inFlight.current = true;
    const requestGeneration = generation.current;
    const requestVersion = ++listVersion.current;
    setLoading(true);
    try {
      const reservations: Reservation[] = [];
      let nextToken: string | null | undefined;
      const tokens = new Set<string>();
      do {
        const response: { data?: GetReservationsPageQuery; errors?: unknown[] } = await client.graphql({
          query: getReservationsPage,
          variables: { nextToken },
        });
        if (generation.current !== requestGeneration || listVersion.current !== requestVersion) return;
        if (response.errors?.length || !response.data?.getReservationsPage) throw new Error('List unavailable');
        const page = response.data.getReservationsPage;
        for (const event of page.items || []) if (event) reservations.push(event);
        nextToken = page.nextToken;
        if (nextToken && tokens.has(nextToken)) throw new Error('Repeated page');
        if (nextToken) tokens.add(nextToken);
      } while (nextToken);
      if (generation.current !== requestGeneration || listVersion.current !== requestVersion) return;
      const changed = reservations.filter(event => previous.current && previous.current.get(event.ReservationHash) !== event.Status && ['succeeded', 'failed', 'unknown'].includes(event.Status || ''));
      if (changed.length) setNotice(changed.map(event => `${patientDetails(event.AppointmentData).patient || '予約'}：${statusDetails(event).label}`).join('、'));
      previous.current = new Map(reservations.map(event => [event.ReservationHash, event.Status]));
      setEvents(reservations.sort((a, b) =>
        b.ReservationDate.localeCompare(a.ReservationDate)
        || patientDetails(b.AppointmentData).partOfDay.localeCompare(patientDetails(a.AppointmentData).partOfDay)
      ));
      setError('');
    } catch {
      if (generation.current === requestGeneration && listVersion.current === requestVersion) setError('予約一覧を取得できませんでした。ログイン状態と接続を確認して更新してください。表示中の結果は最新でない可能性があります。');
    } finally {
      if (generation.current === requestGeneration && listVersion.current === requestVersion) {
        inFlight.current = false;
        setLoading(false);
      }
    }
  }, [client]);

  useEffect(() => {
    generation.current += 1;
    inFlight.current = false;
    previous.current = null;
    setEvents([]);
    setNotice('');
    setError('');
    deletingRef.current = false;
    setDeleting(null);
    setLoading(Boolean(client));
    void fetchEvents();
    return () => {
      generation.current += 1;
    };
  }, [client, fetchEvents]);

  useEffect(() => {
    if (session.kind === 'ready') void fetchEvents();
  }, [session.kind, fetchEvents]);

  const handleDelete = async (event: Reservation) => {
    if (!client || sessionKind.current !== 'ready' || deletingRef.current || !canCancel(event)) return;
    if (!window.confirm('この自動予約の実行予定を取り消しますか？ 医院で成立した予約のキャンセルはできません。')) return;
    const requestGeneration = generation.current;
    deletingRef.current = true;
    setDeleting(event.ReservationHash);
    try {
      const response = await client.graphql({ query: deleteReservation, variables: { ReservationHash: event.ReservationHash } });
      if (generation.current !== requestGeneration) return;
      if (!response.data?.deleteReservation?.success) throw new Error('Delete rejected');
      setNotice('自動予約の実行予定を取り消しました。');
      await fetchEvents(true);
    } catch {
      if (generation.current !== requestGeneration) return;
      setError('取り消せませんでした。処理が始まっている可能性があります。一覧を更新して確認してください。');
    } finally {
      if (generation.current === requestGeneration) {
        deletingRef.current = false;
        setDeleting(null);
      }
    }
  };

  return (
    <Container maxWidth="lg" disableGutters>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2} sx={{ mb: 3.5 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, letterSpacing: '-0.04em', mb: 1 }}>予約一覧</Typography>
          <Typography color="text.secondary" variant="body2">通院の予定と、医院への予約結果を確認できます。</Typography>
        </Box>
        <Tooltip title="予約一覧を更新">
          <span>
            <IconButton size="small" aria-label="更新" onClick={() => void fetchEvents()} disabled={loading || !client || session.kind !== 'ready'} sx={{ color: 'primary.main', mt: 0.5 }}>
              <RefreshRoundedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      <Alert severity="info" sx={{ mb: 3 }}>
        「予約成立」が医院への予約完了です。最新の結果は、右上の更新ボタンで確認できます。
      </Alert>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: '20px', overflow: 'hidden' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: { xs: 2.5, md: 3 }, py: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" gap={1.25} alignItems="center">
            <EventNoteRoundedIcon color="primary" />
            <Typography component="h2" variant="h6" sx={{ fontSize: '1rem' }}>予約・実行結果</Typography>
            <Typography variant="body2" color="text.secondary">{events.length}件</Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">新しい日付順</Typography>
        </Stack>
        {loading && <Box role="status" aria-label="予約一覧を更新中…"><LinearProgress sx={{ height: 2 }} /><Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 3, pt: 1 }}>予約一覧を更新中…</Typography></Box>}
        {!loading && !error && events.length === 0 && <Stack alignItems="center" spacing={1.5} sx={{ px: 3, py: 7 }}>
          <Box sx={{ display: 'grid', placeItems: 'center', width: 64, height: 64, borderRadius: '50%', bgcolor: '#E1F1EF', color: 'primary.main', mb: 1 }}><EventNoteRoundedIcon sx={{ fontSize: 30 }} /></Box>
          <Typography variant="h6">予約はありません。</Typography>
          <Typography color="text.secondary" variant="body2" sx={{ textAlign: 'center' }}>新規予約から患者と受診日を選んで、予約代行を登録できます。</Typography>
          <Button href="/reservation/sekine-jibika/exist" variant="contained" sx={{ mt: 1 }}>新規予約へ</Button>
        </Stack>}
        <TableContainer sx={{ position: 'relative', overflowX: 'hidden' }}>
          <Table aria-label="予約一覧" role="table" sx={{
            tableLayout: 'fixed',
            '& th': { bgcolor: '#F7FAFB', color: 'text.secondary', fontSize: '0.75rem', fontWeight: 600, py: 1.5 },
            '& td': { verticalAlign: 'top', py: 3, overflowWrap: 'anywhere' },
            '& tr:last-child td': { borderBottom: 0 },
            '@media (max-width: 899px)': {
              display: 'block',
              '& thead': { position: 'absolute', left: 0, top: 0, width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap' },
              '& tbody': { display: 'block' },
              '& tbody tr': { display: 'grid', gridTemplateColumns: '1fr auto', px: 2.5, py: 2.5, gap: 1, borderBottom: '1px solid', borderColor: 'divider' },
              '& tbody tr:last-child': { borderBottom: 0 },
              '& td': { display: 'block', border: 0, padding: 0 },
              '& td:nth-of-type(1)': { gridColumn: '1', gridRow: '1' },
              '& td:nth-of-type(2)': { gridColumn: '1 / -1', gridRow: '3' },
              '& td:nth-of-type(3)': { gridColumn: '1 / -1', gridRow: '2' },
              '& td:nth-of-type(4)': { gridColumn: '1 / -1', gridRow: '4', pt: 1 },
              '& td:nth-of-type(5)': { gridColumn: '1 / -1', gridRow: '5' },
            },
          }}>
            <TableHead><TableRow role="row">
              <TableCell sx={{ width: '19%' }}>予約日</TableCell><TableCell sx={{ width: '18%' }}>医院</TableCell><TableCell sx={{ width: '17%' }}>氏名・診察券</TableCell><TableCell sx={{ width: '28%' }}>予約結果</TableCell><TableCell sx={{ width: '18%' }}>操作</TableCell>
            </TableRow></TableHead>
            <TableBody>{events.map(event => {
              const details = patientDetails(event.AppointmentData);
              const status = statusDetails(event);
              return <TableRow key={event.ReservationHash} role="row">
                <TableCell role="cell" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{event.ReservationDate}{details.partOfDay === '1' ? ' 午後' : details.partOfDay === '0' ? ' 午前' : ''}</TableCell>
                <TableCell role="cell" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{details.hospital}</TableCell>
                <TableCell role="cell" sx={{ fontWeight: 600 }}>{details.patient || '不明'}</TableCell>
                <TableCell role="cell">
                  <Stack spacing={1} alignItems="flex-start">
                    <Chip label={status.label} color={status.color} size="small" sx={{ fontWeight: 700, px: 0.5 }} />
                    {status.label === '結果未確認' && <Typography variant="body2">予約の成否を確認できません。再予約する前に医院へ確認してください。</Typography>}
                    {event.ResultMessage && <Typography variant="body2" color="text.secondary">{event.ResultMessage}</Typography>}
                    {event.ReceiptNumber && <Typography variant="body2" sx={{ fontWeight: 700 }}>受付番号：{event.ReceiptNumber}</Typography>}
                    {event.ResultUpdatedAt && <Typography variant="caption" color="text.secondary">結果更新：{new Date(event.ResultUpdatedAt).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })}</Typography>}
                  </Stack>
                </TableCell>
                <TableCell role="cell">{canCancel(event) && <Button size="small" variant="text" sx={{ fontSize: '0.75rem', textAlign: 'left' }} onClick={() => void handleDelete(event)} disabled={deleting !== null || session.kind !== 'ready'}>{deleting === event.ReservationHash ? '取り消し中…' : '実行予定を取り消す'}</Button>}</TableCell>
              </TableRow>;
            })}</TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" gap={2} sx={{ mt: 3.5, p: { xs: 2.5, md: 3 }, bgcolor: '#E8F2F4', borderRadius: '16px' }}>
        <Box sx={{ maxWidth: 540 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.75 }}>医院の受付ページでも確認できます</Typography>
          <Typography variant="body2" color="text.secondary">医院の受付ページで午前・午後を選択して、予約確認へ進んでください。</Typography>
        </Box>
        <Button href="https://sekine-jibika.mdja.jp/" target="_blank" rel="noopener noreferrer" variant="outlined" endIcon={<OpenInNewRoundedIcon sx={{ fontSize: '16px !important' }} />} sx={{ bgcolor: 'white', flexShrink: 0, fontSize: '0.8rem' }}>
          せきね耳鼻咽喉科の予約を確認
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, px: 0.5 }}>実行予定の取り消しは、医院で成立した予約のキャンセルではありません。</Typography>
      <Snackbar open={Boolean(notice)} onClose={() => setNotice('')} message={notice} action={<Button color="inherit" onClick={() => setNotice('')}>閉じる</Button>} />
    </Container>
  );
};

export default ReservationConfirm;
